import { Grid, Typography } from '@material-ui/core';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';
import { FormattedMessage } from 'react-intl';
import Select, { components as reactSelectComponents } from 'react-select';
import Creatable from 'react-select/creatable';

import { theme } from 'src/styles/theme';

import Caption from '../../../Typography/Caption';
import messages from '../../messages';
import CustomDropdownIndicator from '../CustomDropdownIndicator';
import CustomControl from '../StyledControl';
import { StyledOption } from '../StyledOption';
import useStyles from './styles';

export const defaultStyles = {
  clearIndicator: provided => ({
    ...provided,
    padding: theme.spacing(0.5, 1),
  }),
  container: (provided, state) => {
    if (state.selectProps.inlineSearch) {
      return {
        background: theme.palette.common.white,
        border: theme.customBorders.dropdown,
        borderRadius: 4,
        boxShadow: theme.customShadows.shadow4,
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
      };
    }
    return null;
  },
  input: provided => ({
    ...provided,
    margin: 0,
  }),
  menu: (provided, state) => ({
    ...provided,
    border: !state.selectProps.inlineSearch && theme.customBorders.gray400,
    boxShadow: !state.selectProps.inlineSearch && theme.customShadows.shadow4,
    marginBottom: 0,
    position: state.selectProps.inlineSearch && 'unset',
  }),
  menuList: provided => ({
    ...provided,
    padding: theme.spacing(1),
  }),
  menuPortal: base => ({ ...base, zIndex: 801 }),
  multiValue: provided => ({
    ...provided,
    '&:hover': {
      backgroundColor: theme.palette.common.blue200,
    },
    alignItems: 'center',
    backgroundColor: theme.palette.common.blue100,
    borderRadius: theme.highlightBorderRadius,
    display: 'flex',
    gap: theme.spacing(1),
    margin: theme.spacing(0.25),
    marginLeft: 'unset',

    padding: theme.spacing(1, 1.75, 1, 1.25),
  }),
  multiValueLabel: provided => ({
    ...provided,
    color: theme.palette.common.blue600,
    padding: 0,
  }),
  multiValueRemove: provided => ({
    ...provided,
    '&:hover': {
      backgroundColor: theme.palette.common.blue100,
      borderRadius: '50%',
      color: theme.palette.common.blue600,
    },
    '> svg': {
      height: theme.spacing(2),
      width: theme.spacing(2),
    },
    color: theme.palette.common.blue600,

    marginBottom: theme.spacing(-0.125),

    padding: 0,
  }),
  singleValue: provided => ({
    ...provided,
    marginLeft: 0,
    marginRight: theme.spacing(1),
  }),
  valueContainer: provided => ({
    ...provided,
    padding: 0,
  }),
};

let generatedIdCounter = 0;

function useGeneratedId(prefix) {
  const idRef = useRef(null);

  if (idRef.current === null) {
    generatedIdCounter += 1;
    idRef.current = `${prefix}-${generatedIdCounter}`;
  }

  return idRef.current;
}

/**
 * react-select v3 hardcodes the aria-* set it writes onto the real input
 * (`Select.renderInput`), so `aria-invalid` and `aria-describedby` handed to
 * `Select` never reach it. Replacing `Input` only covers a searchable select:
 * `isSearchable={false}` renders the internal `DummyInput`, which the
 * `components` map cannot reach. Both render as the last child of
 * `ValueContainer`, so clone that child instead.
 *
 * This wraps a container rather than replacing one, because a consumer may pass
 * a `ValueContainer` of its own and every consumer needs the wiring.
 */
const withAriaInput = ValueContainer => {
  const ValueContainerWithAria = props => {
    const { children, selectProps } = props;
    const describedBy = selectProps['aria-describedby'];
    const isInvalid = Boolean(selectProps.error);
    const childNodes = React.Children.toArray(children);
    const inputIndex = childNodes.length - 1;

    return (
      <ValueContainer {...props}>
        {childNodes.map((child, index) =>
          index === inputIndex && React.isValidElement(child)
            ? React.cloneElement(child, {
                'aria-describedby': describedBy || undefined,
                'aria-invalid': isInvalid || undefined,
              })
            : child,
        )}
      </ValueContainer>
    );
  };

  ValueContainerWithAria.propTypes = {
    children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
    selectProps: PropTypes.object,
  };

  return ValueContainerWithAria;
};

const formatGroupLabel = data => (
  <Grid>
    <Typography variant="h5">{data.label}...</Typography>
  </Grid>
);

function Selector({
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledBy,
  className,
  error,
  inlineSearch,
  isCreatable,
  label,
  menuPlacement,
  noOptionsMessage,
  placeholder,
  size,
  components,
  formatOptionLabel,
  required,
  ...otherProps
}) {
  const classes = useStyles();
  const errorMessageId = useGeneratedId('fds-select-error');
  const labelId = useGeneratedId('fds-select-label');

  const ReactSelect = isCreatable ? Creatable : Select;

  // `error` also accepts `true`, which renders no message for the input to
  // point at.
  const hasErrorMessage = Boolean(error) && error !== true && !inlineSearch;
  const describedBy = [ariaDescribedBy, hasErrorMessage ? errorMessageId : null].filter(Boolean).join(' ') || undefined;

  // `aria-labelledby` wins over `aria-label`, so the rendered label only names
  // the control when the consumer named nothing itself. 87 call sites pass both
  // and 4 of those deliberately differ; this leaves every one of them alone and
  // names the 38 that pass `label` on its own.
  const labelledBy = ariaLabelledBy || (label && !ariaLabel ? labelId : undefined);

  // Keyed on the container itself, not on `components` — that object is an
  // inline literal at most call sites, so a new wrapper type on every render
  // would remount the input and drop focus.
  const consumerValueContainer = components && components.ValueContainer;
  const valueContainer = useMemo(
    () => withAriaInput(consumerValueContainer || reactSelectComponents.ValueContainer),
    [consumerValueContainer],
  );

  const inlineSearchDerivedProps = inlineSearch
    ? { defaultMenuIsOpen: true, inlineSearch, menuIsOpen: true }
    : { inlineSearch, menuPortalTarget: document.body };

  return (
    <Grid className={classNames(classes.selector, className)}>
      {label && (
        <Typography className={classNames(classes.label, { required })} id={labelId} variant="body2">
          {label}
        </Typography>
      )}
      <ReactSelect
        components={{
          Control: CustomControl,
          DropdownIndicator: CustomDropdownIndicator,
          IndicatorSeparator: null,
          LoadingIndicator: null,
          Option: StyledOption,
          ...components,
          ValueContainer: valueContainer,
        }}
        error={error}
        formatGroupLabel={formatGroupLabel}
        formatOptionLabel={formatOptionLabel}
        menuPlacement={menuPlacement}
        noOptionsMessage={noOptionsMessage}
        placeholder={placeholder}
        size={size}
        styles={defaultStyles}
        {...inlineSearchDerivedProps}
        {...otherProps}
        aria-describedby={describedBy}
        aria-label={ariaLabel}
        aria-labelledby={labelledBy}
      />

      {hasErrorMessage && (
        <Caption align="right" classes={{ root: classes.errorMessage }} color="error" id={errorMessageId}>
          {error}
        </Caption>
      )}
    </Grid>
  );
}

Selector.propTypes = {
  'aria-describedby': PropTypes.string,
  'aria-label': PropTypes.string,
  'aria-labelledby': PropTypes.string,
  className: PropTypes.string,
  components: PropTypes.object,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  formatOptionLabel: PropTypes.func,
  inlineSearch: PropTypes.bool,
  isCreatable: PropTypes.bool,
  label: PropTypes.node,
  menuPlacement: PropTypes.oneOf(['auto', 'bottom', 'top']),
  noOptionsMessage: PropTypes.func,
  placeholder: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  required: PropTypes.bool,
  size: PropTypes.string,
};

Selector.defaultProps = {
  className: null,
  isCreatable: false,
  label: null,
  menuPlacement: 'auto',
  noOptionsMessage: () => <FormattedMessage {...messages.noOptionsMessage} />,
  placeholder: <FormattedMessage {...messages.placeholder} />,
  size: 'medium',
};

export default Selector;
